import React from 'react';

import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockListHeader from '../components/GuideMockListHeader';
import GuideMockRadioGroup from '../components/GuideMockRadioGroup';
import GuideMockTextField from '../components/GuideMockTextField';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideClientRegister: React.FC = () => {
  return (
    <GuidePageLayout
      title="新規取引先を登録する"
      description="新しい取引先企業を登録します。"
      breadcrumbs={[{ label: '取引先管理', path: '/guide/client/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '要員管理メニューから取引先管理画面を開く' },
            {
              label: '一覧パネル右上の「+」アイコンを押下して登録フォームを開く',
              content: (
                <GuideMockContainer label="リストヘッダー">
                  <GuideMockListHeader createLabel="取引先新規作成" />
                </GuideMockContainer>
              ),
            },
            {
              label: '取引先情報を入力する',
              description:
                'フォームタイトルは「取引先登録」です。タイトル左の戻る矢印をクリックすると、登録を破棄して一覧に戻ります。',
              content: (
                <GuideMockContainer label="登録フォーム">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        取引先登録
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <GuideMockTextField label="取引先名" required />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="取引先名カナ" required />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockRadioGroup
                          label="取引先区分"
                          options={['SIer', 'エンドユーザー', 'SES']}
                          required
                        />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="取引開始日（年度月選択）" required />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="取引終了日（年度月選択）" />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="法人番号" />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="登録番号" />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockTextField label="住所" />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockTextField label="企業ホームページURL" />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="担当者名" />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="電話番号" />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="メールアドレス" />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockTextField label="備考（6行表示）" />
                      </Grid>
                    </Grid>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「作成」ボタンを押下する',
              content: (
                <GuideMockContainer label="フッターボタン">
                  <GuideMockButton label="作成" startIcon={<AddBusinessIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="入力項目">
        <GuideTable
          headers={['項目名', '必須', '形式', '備考']}
          rows={[
            ['取引先名', '必須', 'テキスト（最大300桁）', ''],
            ['取引先名カナ', '必須', 'テキスト（最大300桁）', 'カタカナのみ'],
            ['取引先区分', '必須', 'ラジオボタン', 'マスタ区分から選択'],
            ['取引開始日', '必須', '年度月選択', 'FinancialYearMonthSelectで選択'],
            ['取引終了日', '—', '年度月選択', '新規作成時は入力不可'],
            ['法人番号', '任意', 'テキスト（13桁）', ''],
            ['登録番号', '任意', 'テキスト（14桁）', 'インボイス番号（T+13桁）'],
            ['住所', '任意', 'テキスト（最大500桁）', ''],
            ['企業ホームページURL', '任意', 'テキスト（最大500桁）', ''],
            ['担当者名', '任意', 'テキスト（最大300桁）', '取引先側窓口'],
            ['電話番号', '任意', 'テキスト（最大20桁）', ''],
            ['メールアドレス', '任意', 'テキスト（最大300桁）', ''],
            ['備考', '任意', 'テキストエリア（最大10000桁）', '6行表示'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印（タイトル左）', '登録を破棄して一覧画面に戻る'],
            ['作成ボタン', '入力内容で取引先を登録する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>取引先IDは自動的に発行されます。</GuideInfoBox>
          <GuideInfoBox>
            取引先を登録した後、プロジェクト登録で取引先として選択できるようになります。
          </GuideInfoBox>
          <GuideInfoBox>
            取引開始日・取引終了日は年度月選択（FinancialYearMonthSelect）から選択します。新規作成時は取引終了日は入力できません。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideClientRegister;
